/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Link, PageProps, graphql } from 'gatsby';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { format, parseISO } from 'date-fns';
import { t } from 'i18next';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import { Trans } from 'react-i18next';
import PageSEO from '../components/PageSEO';
import { LocalizedSEO } from '../graphql-fragments/seo';
import Button from '../components/ui/Button';
import ButtonLink from '../components/ui/ButtonLink';
import SanityLayout from '../components/ui/SanityLayout';
import {
  ALL_LANGS,
  BOOKINGKIT_BRAND,
  BOOKINGKIT_T,
  CW_BOOKINGKIT_ID,
  EXPERIENCES_PREFIX_URL,
  SCRIPT_BOOKINGKIT_ID,
  SITE_BASE_URL,
} from '../constants';
import { SanityImageType } from '../graphql-fragments/SanityImage';
import i18n from '../translation/i18n';
import { withI18n } from '../utils/hocs';
import { useDomObserver } from '../utils/hooks';
import { sortBy } from '../utils/nodash';
import { areSameDay } from '../utils/projectUtils';
import { localize } from '../utils/sanity';
import bookingKitClient, { EventDetailsWithDates } from '../vendorApis/bookingkit';
import { buildEventSchema, buildTouristAttractionSchema } from '../utils/structuredData';
import { urlJoin } from '../utils/utils';
import { DateWithId } from '../components/ui/modules/ExperiencesBookingModule';
import * as styles from './ExperiencePage.module.scss';
import { BackgroundColorTheme } from './Page';

// Calendar pulls in react-day-picker (~9.7 KB gzipped) and is only rendered
// once the user opens the date picker. Lazy so it doesn't ship in the app
// bundle on first paint.
const Calendar = React.lazy(() =>
  import('../components/ui/Calendar').then(m => ({ default: m.Calendar })),
);

export const query = graphql`
  fragment Experience on SanityExperience {
    title {
      ...LocaleStringFragment
    }
    slug {
      current
    }
    bookingKitId
    theme
    image {
      ...SanityImage
    }
    description {
      ...LocaleTextFragment
    }
    seo {
      ...SEO
    }
  }
  query GetExperienceById($id: String!) {
    sanityExperience(_id: { eq: $id }) {
      ...Experience
    }
  }
`;

export interface Experience {
  title: string;
  slug: {
    current: string;
  };
  bookingKitId: string;
  theme: BackgroundColorTheme;
  image: SanityImageType;
  description: string;
  seo?: LocalizedSEO;
}

type ExperiencePageData = {
  sanityExperience: Experience;
};

type ExperiencePageContext = {
  experiencesPagePath: string | null;
};

const ExperiencePage = ({
  data,
  pageContext,
}: PageProps<ExperiencePageData, ExperiencePageContext>): React.ReactElement => {
  const experiencesPagePath = pageContext.experiencesPagePath || EXPERIENCES_PREFIX_URL;
  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: ExperiencePageData = localize(data, langs);

  const { title, description, image, theme, bookingKitId, seo } = localizedData.sanityExperience;
  // Start undefined until we check the url params, and use this to ensure we only
  // run the code that adds the book date button after we checked the url.
  const [selectedDate, setSelectedDate] = useState<DateWithId | null | undefined>(undefined);
  const [
    [bookingKitBookDateButtonContainerMobile, bookingKitBookDateButtonContainerDesktop],
    setBookingKitBookDateButtonContainers,
  ] = useState<[HTMLDivElement, HTMLDivElement] | [null, null]>([null, null]);
  const [bookingKitContainer, setBookingKitContainerRef] = useState<HTMLDivElement | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const bookNowRef = useRef<HTMLAnchorElement | null>(null);
  const initBkAttemptsRef = useRef(0);

  const [experience, setExperience] = useState<EventDetailsWithDates | null>(null);
  const availableDatesWithIds = experience
    ? sortBy(
        experience.dates.map(date => ({ date: new Date(date.date), id: date.id })),
        date => date.date.getTime(),
      )
    : null;

  const experienceUrl = urlJoin(
    SITE_BASE_URL,
    EXPERIENCES_PREFIX_URL,
    localizedData.sanityExperience.slug.current,
  );

  const eventSchemas = useMemo(() => {
    if (!experience) return [];
    const priceTier = experience.prices[0];
    const priceValue = priceTier ? Number(priceTier.value) : undefined;
    const priceCurrency = priceTier?.currency;
    const MAX_EVENTS = 50;
    return experience.dates.slice(0, MAX_EVENTS).map(d =>
      buildEventSchema({
        name: title,
        description,
        imageUrl: image?.asset?.url,
        url: experienceUrl,
        startDate: new Date(d.date).toISOString(),
        locationName: title,
        offers: [
          {
            url: `https://eu5.bookingkit.de/checkout2/addToCart/?eventDate_id=${d.id}&t=${BOOKINGKIT_T}&cw=${CW_BOOKINGKIT_ID}`,
            price: Number.isFinite(priceValue) ? priceValue : undefined,
            priceCurrency,
            availability:
              d.available_slots > 0
                ? 'https://schema.org/InStock'
                : 'https://schema.org/SoldOut',
          },
        ],
        organizerName: 'Dinner in the Sky',
        organizerUrl: urlJoin(SITE_BASE_URL, '/'),
      }),
    );
  }, [experience, title, description, image?.asset?.url, experienceUrl]);

  function getAvailableDateWithId(date: Date) {
    return availableDatesWithIds!.find(availableDate => areSameDay(date, availableDate.date));
  }

  const bookingKitCalendarContainer = useDomObserver({
    selector: '.bk-header-calendar',
    root: bookingKitContainer || undefined,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dateStr = params.get('date');
    const dateId = params.get('dateId');
    if (dateStr && dateId) {
      const date = parseISO(dateStr);
      setSelectedDate({
        date,
        id: dateId,
      });
    } else {
      setSelectedDate(null);
      params.delete('date');
      params.delete('dateId');
      const query = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (query ? `?${query}` : ''),
      );
    }
  }, []);

  useEffect(() => {
    if (selectedDate !== undefined) {
      // Fetch current experience from API to check available dates
      void (async () => {
        try {
          const apiExperience = await bookingKitClient.getEventDetails(bookingKitId, {
            withDates: true,
          });
          setExperience(apiExperience);
          // If date in url params is no longer avaible, clean it
          if (selectedDate && !apiExperience.dates.find(date => date.id === selectedDate.id)) {
            resetDate();
          }
        } catch (err) {
          console.error(err);
        }
      })();

      const MAX_INIT_ATTEMPTS = 100; // ~30 seconds at 300ms intervals
      const interval = setInterval(() => {
        initBkAttemptsRef.current++;
        if (typeof window.initBk === 'function' && typeof window.jQuery !== 'undefined') {
          window.initBk();
          const buttonsContainerMobile = document.querySelector<HTMLDivElement>(
            '#bookingKitContainer .only-mobile .bk-detail-buttons',
          );
          const buttonsContainerDesktop = document.querySelector<HTMLDivElement>(
            '#bookingKitContainer .no-mobile .bk-detail-buttons',
          );
          if (buttonsContainerMobile && buttonsContainerDesktop) {
            const params = new URLSearchParams(window.location.search);
            const dateId = params.get('dateId');
            if (dateId) {
              // Prepend portal containers so that book date buttons go above other ones
              const mobilePortalContainer = document.createElement('div');
              mobilePortalContainer.classList.add(styles.bookDateButtonContainer);
              buttonsContainerMobile.prepend(mobilePortalContainer);
              const desktopPortalContainer = document.createElement('div');
              desktopPortalContainer.classList.add(styles.bookDateButtonContainer);
              buttonsContainerDesktop.prepend(desktopPortalContainer);
              setBookingKitBookDateButtonContainers([
                mobilePortalContainer,
                desktopPortalContainer,
              ]);
            }

            clearInterval(interval);
            return;
          }
        }

        if (initBkAttemptsRef.current > MAX_INIT_ATTEMPTS) {
          const hasInitBk = typeof window.initBk === 'function';
          const hasJQuery = typeof window.jQuery !== 'undefined';
          Sentry.captureException(
            new Error(
              `Failed to initialize BookingKit widget after ${MAX_INIT_ATTEMPTS} attempts. ` +
                `initBk: ${hasInitBk}, jQuery: ${hasJQuery}`,
            ),
          );
          clearInterval(interval);
        }
      }, 300);
    }
  }, [selectedDate === undefined]);

  const today = useMemo(() => new Date(), []);

  const resetDate = () => {
    setSelectedDate(null);
    setBookingKitBookDateButtonContainers([null, null]);
    document.querySelectorAll('.' + styles.bookDateButtonContainer).forEach(container => {
      container.remove();
    });
    const params = new URLSearchParams(window.location.search);
    params.delete('date');
    params.delete('dateId');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : ''),
    );
  };

  function openCheckoutForSelectedDate(e = { preventDefault: () => {} }) {
    if (typeof window.BookingKitApp !== 'undefined' && bookNowRef.current) {
      window.BookingKitApp.cartLinkClick(e, bookNowRef.current);
    }
  }

  const handleDateSelect = (date?: Date) => {
    if (!date) {
      Sentry.captureException('Called handleDateSelect without date in ExperiencePage');
      resetDate();
    } else {
      setSelectedDate(getAvailableDateWithId(date));
      setTimeout(() => {
        openCheckoutForSelectedDate();
      }, 200);
    }
    setIsDatePickerOpen(false);
  };

  function renderCalendarFooter(): React.ReactNode {
    return (
      <div className={styles.unavailabelDateContainer}>
        <div className={styles.unavailableDate}>
          {t('experience.is_your_date_unavailable', 'Is your date not available?')}
        </div>
        <Link to={'/' + experiencesPagePath} className={styles.exploreOtherExperiences}>
          {t('experience.explore_other_experiences', 'Explore other Experiences')}
        </Link>
      </div>
    );
  }

  function renderBookDateButton(): React.ReactNode {
    return (
      <>
        <ButtonLink
          className={styles.bookButton}
          ref={bookNowRef}
          href={`https://eu5.bookingkit.de/checkout2/addToCart/?eventDate_id=${
            selectedDate!.id
          }&t=${BOOKINGKIT_T}&cw=${CW_BOOKINGKIT_ID}`}
          rel="noopener noreferrer"
          data-action="addToCart"
          data-gabrand={BOOKINGKIT_BRAND}
          data-gaquantity="1"
          onClick={e => {
            e.preventDefault();
            openCheckoutForSelectedDate(e);
          }}
        >
          {t('experience.book_now', 'Book {{selectedDate}} now', {
            selectedDate: format(selectedDate!.date, 'MMMM do'),
          })}
        </ButtonLink>
        {availableDatesWithIds && availableDatesWithIds.length > 1 && (
          <>
            <Button
              secondary
              className={styles.changeDateLink}
              onClick={e => {
                e.preventDefault();
                setIsDatePickerOpen(!isDatePickerOpen);
              }}
            >
              {t('exprience.book_other_date', 'Book other date')}
            </Button>
            {isDatePickerOpen && (
              <Suspense fallback={null}>
              <Calendar
                required
                fromYear={today.getFullYear()}
                toYear={today.getFullYear()}
                fromMonth={
                  today.getTime() < availableDatesWithIds[0].date.getTime()
                    ? today
                    : availableDatesWithIds[0].date
                }
                toMonth={availableDatesWithIds.at(-1)?.date}
                defaultMonth={selectedDate?.date}
                disabled={date => !getAvailableDateWithId(date)}
                selected={selectedDate?.date}
                onSelect={handleDateSelect}
                footer={renderCalendarFooter()}
              ></Calendar>
              </Suspense>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <SanityLayout pageTheme={theme} lastModuleTheme={theme} frontCloudPosition="top">
      <Helmet>
        <meta name="referrer" content="strict-origin-when-cross-origin" />
        <link
          rel="preconnect"
          href={`https://${SCRIPT_BOOKINGKIT_ID}.widget.bookingkit.net`}
        />
        <script
          src={`https://${SCRIPT_BOOKINGKIT_ID}.widget.bookingkit.net/bkscript/${CW_BOOKINGKIT_ID}/?lang=${i18n.language}&e=${bookingKitId}`}
          async
        ></script>
      </Helmet>
      <PageSEO
        pageSEO={seo}
        defaultTitle={title}
        defaultDescription={description}
        defaultImageUrl={image?.asset?.url}
        structuredData={[
          buildTouristAttractionSchema({
            name: title,
            description,
            imageUrl: image?.asset?.url,
            url: experienceUrl,
          }),
          ...eventSchemas,
        ]}
      />
      <div className={styles.container} data-theme={theme}>
        {availableDatesWithIds && availableDatesWithIds.length === 0 && (
          <div className={styles.soldOutForSeasonContainer}>
            <span className={styles.soldOutTitle}>
              {t('experience.coming_soon', 'Coming soon')}
            </span>
            <div className={styles.soldOutText}>
              <p>
                {t(
                  'experience.experience_available_soon',
                  'This experience will be available soon.',
                )}
              </p>
              <p>
                <Trans
                  i18nKey="experience.check_other_experiences"
                  defaults="Check our other experiences <experiencesLink>here</experiencesLink>."
                  components={{
                    experiencesLink: <Link to={'/' + experiencesPagePath}>_</Link>,
                  }}
                />
              </p>
            </div>
          </div>
        )}
        <div className={styles.contentContainer}>
          <div
            ref={setBookingKitContainerRef}
            id="bookingKitContainer"
            data-lang={i18n.language}
            data-e={bookingKitId}
            data-cw={CW_BOOKINGKIT_ID}
            className={styles.bookingKit}
          >
            <div className={styles.spinner}>
              <svg viewBox="25 25 50 50">
                <circle cx="50" cy="50" r="20" fill="none" className={styles.path}></circle>
              </svg>
            </div>
          </div>
          {bookingKitBookDateButtonContainerMobile &&
            createPortal(renderBookDateButton(), bookingKitBookDateButtonContainerMobile)}
          {bookingKitBookDateButtonContainerDesktop &&
            createPortal(renderBookDateButton(), bookingKitBookDateButtonContainerDesktop)}
          {bookingKitCalendarContainer &&
            createPortal(renderCalendarFooter(), bookingKitCalendarContainer)}
        </div>
      </div>
    </SanityLayout>
  );
};

export default withI18n(ExperiencePage);
